import React, { useEffect, useState } from 'react';
import {
  getLeadByID,
  getCloseDealsById,
  getAllProjects,
  addFollowUp,
  updateFollowUp,
  createClosedDeal,
  createUnderConsideration,
  updateStage,
  updateLead,
  cloneLead,
  deleteLead,
  handleCheckLeadDelete,
  getAllAccommodationsByProject,
  updateFollowUpDate,
  addLog,
  getStageQualificationRequired,
  getAiLeadInsight,
  getLatestStageQualification,
  getStageQualificationProfile,
  getApplication,
  getClosedDealAttachmentsByDealId
} from '../services/api/implementation/impl';
import { evaluateKycCompleteness } from '../components/postSales/postSalesKyc';
import {
  getDealPricingCompare,
  formatDealMoney,
  formatDealRatePerSqft,
  ratePerAreaLabel,
  resolveDealRateAreaUnit
} from '../utils/dealPricingCompare';
import Multiselect from 'multiselect-react-dropdown';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useForm, Controller } from 'react-hook-form';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import toast from 'react-hot-toast';
import axios from 'axios';
import {
  Building2,
  Calendar,
  Plus,
  X,
  Pencil,
  Info,
  MapPin,
  Flame,
  User,
  Receipt,
  MoveRight,
  Copy,
  Trash2,
  Activity,
  ClipboardList
} from 'lucide-react';
import { ClosedWonCloseDeal } from '../components/forms/ClosedWonCloseDeal';
import { UnderConsiderationCreate } from '../components/forms/UnderConsiderationCreate';
import EditLeadForm from '../components/forms/EditLeadForm';
import EditContactProfileForm from '../components/forms/EditContactProfileForm';
import { getLeadAssociatedUnitLabel } from '../utils/getLeadAssociatedUnit';
import AiInsightsCard from '../components/ai/AiInsightsCard';
import LeadTemperatureMovementCard from '../components/ai/LeadTemperatureMovementCard';
import StageQualificationModal from '../components/forms/StageQualificationModal';
import NurtureRecommendationBanner from '../components/forms/NurtureRecommendationBanner';
import ExpandableProfileSection, {
  ProfileRow,
  ProfilePreviewGrid,
  buildPreviewItems,
  TimelinePreview
} from '../components/lead/ExpandableProfileSection';
import PreleadOriginalFormModal from '../components/prelead/PreleadOriginalFormModal';
import { STAGE_HISTORY_AND_LOGS_TAB } from '../constants/leadDetailTabs';
import { getLeadRemark } from '../utils/getLeadRemark';
import { getLeadSourceDisplay } from '../utils/getLeadSourceDisplay';
import { getNextFollowUpForLead } from '../utils/getNextFollowUpForLead';
import {
  isFutureProspectStage,
  isDemoScheduledStage,
  isUpcomingContactStage,
  isValidUpcomingContactDate,
  requiresNextFollowUpFields,
  requiresClosureNote,
  closureNoteLabel,
  closureNotePlaceholder,
  upcomingContactDateLabel,
  upcomingContactDateRequiredToast,
  upcomingContactPastDateToast,
  upcomingAgendaLabel,
  upcomingAgendaPlaceholder,
  upcomingAgendaRequiredToast,
  upcomingContactHelperText
} from '../utils/requiresNextFollowUpFields';

export { STAGE_HISTORY_AND_LOGS_TAB };

/** Shared 60/40 column split — AI Insights profile rail + overview sidebar */
const OVERVIEW_COLUMN_GRID = 'xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]';

const AI_ACTION_DISPLAY_MAX = 72;

function compactActionLabel(text, maxLen = AI_ACTION_DISPLAY_MAX) {
  const normalized = String(text).replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  const firstSentence = normalized.split(/[.;]/)[0].trim();
  if (firstSentence.length <= maxLen) return firstSentence;
  const slice = firstSentence.slice(0, maxLen);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > maxLen * 0.5 ? slice.slice(0, lastSpace) : slice;
  return `${cut.trim()}…`;
}

function cleanAiActionLabel(action) {
  if (!action) return null;
  const cleaned = String(action)
    .replace(/\([^)]*new lead has been created[^)]*\)/gi, '')
    .replace(/\s*—\s*follow-up was due\s+[\d-]+/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (!cleaned) return null;
  return compactActionLabel(cleaned);
}

function PricingDetailsSection({ title, subtitle, children }) {
  return (
    <div className="px-2 md:px-4 mb-4">
      <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04]">
        <div className="border-b border-slate-100/80 bg-gradient-to-r from-teal-50 via-white to-slate-50 px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex items-start gap-3">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-700/12 text-teal-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
              aria-hidden>
              <Receipt className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
              {subtitle ? (
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{subtitle}</p>
              ) : null}
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-6">{children}</div>
      </div>
    </div>
  );
}

function PricingMetricTile({ label, value, emphasis, labelActions }) {
  return (
    <div
      className={
        emphasis
          ? 'relative flex min-h-[7.5rem] flex-col justify-between rounded-xl border border-teal-200/90 bg-gradient-to-br from-teal-50/90 via-white to-teal-50/40 p-4 shadow-[0_2px_16px_rgba(15,118,110,0.1)] ring-1 ring-teal-800/10'
          : 'relative flex min-h-[6.5rem] flex-col justify-between rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm transition-all hover:border-slate-300/90 hover:shadow-md'
      }>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        {labelActions ? <div className="shrink-0">{labelActions}</div> : null}
      </div>
      <p
        className={`mt-3 tabular-nums font-semibold text-slate-900 ${
          emphasis ? 'text-xl text-teal-900 sm:text-2xl' : 'text-base sm:text-lg'
        }`}>
        {value}
      </p>
    </div>
  );
}

const SaleOverview = ({
  leadID,
  type,
  activeHeaderTab = 'Overview',
  onHeaderTabChange,
  combineHistoryAndLogsTabs = false,
  dealPricingDetailsToolbar = null
}) => {
  /* 
    Header data
  */

  const { id } = useParams();
  const navigate = useNavigate();
  const userId = Cookies.get('userId');
  const userVertical = String(Cookies.get('userVertical') || '').toLowerCase();
  const userDesignationLevel = Cookies.get('userDesignationLevel');
  const canAddStandaloneNote =
    userVertical === 'sales' || userVertical === 'management';
  const canEditContactProfile =
    userVertical === 'sales' || userVertical === 'management';
  const canDeleteLeads =
    userVertical === 'management' ||
    (userVertical === 'sales' &&
      (userDesignationLevel === 'Medium' || userDesignationLevel === 'High'));

  const ID = leadID ? leadID : id;

  // Follow up modal states
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [log, setLog] = useState('');
  const [stage, setStage] = useState('');
  const [agenda, setAgenda] = useState('');
  const [otherLog, setOtherLog] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedStage, setSelectedStage] = useState('');
  const [isCloseDealModalOpen, setIsCloseDealModalOpen] = useState(false);
  const [isUnderConsiderationModalOpen, setIsUnderConsiderationModalOpen] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [stageToUpdate, setStageToUpdate] = useState('');
  const [isUpdatingStage, setIsUpdatingStage] = useState(false);
  const [ucDetails, setUcDetails] = useState({
    project: null,
    block: null,
    level: null,
    unit: null
  });
  const [projectAccommodations, setProjectAccommodations] = useState([]);
  const [qualModalOpen, setQualModalOpen] = useState(false);
  const [qualSubmitting, setQualSubmitting] = useState(false);
  const [pendingQualAction, setPendingQualAction] = useState(null);
  const [wantStageQualification, setWantStageQualification] = useState(false);

  // Follow up modal states
  const [showUpdateFollowUpModal, setShowUpdateFollowUpModal] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [isUpdatingFollowUp, setIsUpdatingFollowUp] = useState(false);
  const [showExtraChargesBreakdownModal, setShowExtraChargesBreakdownModal] = useState(false);
  const [isEditLeadModalOpen, setIsEditLeadModalOpen] = useState(false);
  const [showPreleadFormModal, setShowPreleadFormModal] = useState(false);
  const [isEditContactProfileOpen, setIsEditContactProfileOpen] = useState(false);
  const [isUpdatingLeadDetails, setIsUpdatingLeadDetails] = useState(false);
  const [isUpdatingContactProfile, setIsUpdatingContactProfile] = useState(false);
  const [isCloneLeadModalOpen, setIsCloneLeadModalOpen] = useState(false);
  const [selectedCloneProjects, setSelectedCloneProjects] = useState([]);
  const [canDelete, setCanDelete] = useState(null);
  const [deleteResData, setDeleteResData] = useState(null);
  const [isDeletingLead, setIsDeletingLead] = useState(false);
  const [aiFollowUpOpen, setAiFollowUpOpen] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [standaloneNote, setStandaloneNote] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const queryClient = useQueryClient();

  const {
    control,
    setValue,
    formState: { errors }
  } = useForm();

  // Stage options from Leads.jsx
  const stageOptions = [
    { id: 'Phone Call Initiated', title: 'Phone Call Initiated (Cold)' },
    { id: 'Approached', title: 'Approached (Cold)' },
    { id: 'Demo Scheduled', title: 'Demo Scheduled (Upcoming demo)' },
    { id: 'Presentation', title: 'Presentation (Warm)' },
    { id: 'Site Visited', title: 'Site Visited (Warm)' },
    { id: 'Paused / Nurture', title: 'Paused / Nurture (Inactive pipeline)' },
    { id: 'Future Prospect', title: 'Future Prospect (Contact later)' },
    { id: 'Commercials Shared', title: 'Commercials Shared (Hot)' },
    { id: 'Negotiation', title: 'Negotiation (Hot)' },
    { id: 'Under Consideration', title: 'Under Consideration (Confirmed)' },
    { id: 'Deal Closed', title: 'Deal Closed (Closed Won)' },
    { id: 'Deal Dropped', title: 'Deal Dropped (Closed Lost)' }
  ];

  // Log options from Leads.jsx
  const logOptions = [
    'Call Not Connected',
    'Switched Off',
    'Not Reachable',
    'Number Busy',
    'No Answer',
    'Wrong Number',
    'Call Connected (Not Interested)',
    'Call Connected (Interested)',
    'Call Connected (Call Back Later)',
    'Call Connected (Sent WhatsApp)',
    'Call Connected (Sent SMS)',
    'Call Connected (Shared Brochure)',
    'Call Connected (Meeting Scheduled)',
    'Demo Proposed',
    'Demo Scheduled',
    'Client Requested Demo',
    'Call Connected (Shared Pricing Info)',
    'Call Connected (Language Barrier)',
    'Call Connected (Decision Maker Not Available)',
    'Follow-up Tomorrow',
    'Follow-up in 3 Days',
    'Follow-up Next Week',
    'Follow-up After Site Visit',
    'Follow-up After Family Discussion',
    'Follow-up After Festival/Holiday',
    'Awaiting Callback from Customer',
    'Looking for Plot',
    'Looking for Flat',
    'Looking for Commercial',
    'Finalizing Options',
    'Just Exploring',
    'Budget Too Low',
    'Referred by Friend/Family',
    'Wants Site Visit',
    'Site Visit Done (Awaiting Feedback)',
    'Site Visit Done (Not Interested)',
    'Booked with Another Project',
    'Booked with Us',
    'Already Visited Office',
    'Project Not in Preferred Locality',
    'Other'
  ];

  // Track component mount

  // Track page view
  useEffect(() => {
    mixpanel.track('Sale Overview Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    mixpanel.track('Sale Overview Viewed', {
      user_id: userId,
      lead_id: ID,
      timestamp: new Date().toISOString()
    });
  }, [ID]);

  const {
    data: leadbyId,
    isLoading,
    error,
    refetch: refetchLeadbyId
  } = useQuery({
    queryKey: ['leadById', parseInt(ID)],
    queryFn: async () => {
      const res = await getLeadByID(parseInt(ID));
      return res;
    },
    enabled: !!ID, // Only run the query if id is truthy
    onSuccess: (data) => {
      mixpanel.track('Sale Overview Lead API Success', {
        user_id: userId,
        lead_id: ID,
        lead_name: data?.user?.name,
        lead_status: data?.pstatus,
        assigned_agent: data?.sales?.name,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Sale Overview Lead API Failed', {
        user_id: userId,
        lead_id: ID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const leadIdNum = Number(ID);

  const invalidateLeadAiCaches = () => {
    if (!Number.isFinite(leadIdNum)) return;
    queryClient.invalidateQueries({ queryKey: ['aiLeadInsight', leadIdNum] });
    queryClient.invalidateQueries({ queryKey: ['aiLeadWhatsappTemplates', leadIdNum] });
    queryClient.invalidateQueries({ queryKey: ['latestStageQualification', leadIdNum] });
    queryClient.invalidateQueries({ queryKey: ['stageQualificationProfile', leadIdNum] });
  };

  const { data: aiInsight, isLoading: aiInsightLoading } = useQuery({
    queryKey: ['aiLeadInsight', leadIdNum],
    queryFn: () => getAiLeadInsight(leadIdNum),
    enabled: !!ID && Number.isFinite(leadIdNum),
    staleTime: 15 * 60 * 1000
  });

  const { data: latestQualification } = useQuery({
    queryKey: ['latestStageQualification', leadIdNum],
    queryFn: () => getLatestStageQualification(leadIdNum),
    enabled: !!ID && Number.isFinite(leadIdNum),
    staleTime: 30_000
  });

  const { data: qualificationProfileData } = useQuery({
    queryKey: ['stageQualificationProfile', leadIdNum],
    queryFn: () => getStageQualificationProfile(leadIdNum),
    enabled: !!ID && Number.isFinite(leadIdNum),
    staleTime: 30_000
  });

  const dealIdForCloseDeal =
    leadbyId?.deal?.id ?? leadbyId?.activities?.find((a) => a.name === 'Deal Closed')?.refrence_id;
  const { data: closeDealData } = useQuery({
    queryKey: ['closeDealsById', dealIdForCloseDeal],
    queryFn: () => getCloseDealsById(dealIdForCloseDeal),
    enabled: type === 'deal' && !!dealIdForCloseDeal
  });

  const { data: dealApplicants } = useQuery({
    queryKey: ['postSalesApplicants', dealIdForCloseDeal],
    queryFn: () => getApplication(),
    enabled: type === 'deal' && !!dealIdForCloseDeal,
    select: (rows) =>
      (rows || []).filter((r) => Number(r.deal_id) === Number(dealIdForCloseDeal))
  });

  const { data: dealKycAttachments } = useQuery({
    queryKey: ['closedDealAttachments', dealIdForCloseDeal],
    queryFn: () => getClosedDealAttachmentsByDealId(dealIdForCloseDeal),
    enabled: type === 'deal' && !!dealIdForCloseDeal
  });

  const postSalesKyc =
    type === 'deal'
      ? evaluateKycCompleteness(dealKycAttachments?.attachments ?? [])
      : null;

  const { data: projects } = useQuery({
    queryKey: ['allProjectsForLeadEdit'],
    queryFn: getAllProjects
  });

  useEffect(() => {
    refetchLeadbyId();
  }, [leadbyId]);


  // Update ucDetails when leadbyId changes (similar to CustomerList.jsx)
  useEffect(() => {
    const unit = leadbyId?.deal?.unit || leadbyId?.underConsideration?.unit;
    if (unit) {
      setUcDetails({
        project: unit.project_id,
        block: unit.block_id,
        level: unit.level_id,
        unit: unit.id
      });
    } else {
      setUcDetails({ project: null, block: null, level: null, unit: null });
    }
  }, [leadbyId]);

  // Fetch project accommodations based on lead's project
  const { data: projectAccommodationsData } = useQuery({
    queryKey: ['project_accommodations', leadbyId?.interested_projects?.[0]],
    queryFn: () => getAllAccommodationsByProject(leadbyId?.interested_projects?.[0]),
    enabled: !!leadbyId?.interested_projects?.[0],
    onError: (error) => {
      console.error('Error fetching project accommodations:', error);
    }
  });

  // Prefill project accommodations from lead data when available
  useEffect(() => {
    if (
      leadbyId?.accomodations &&
      Array.isArray(leadbyId.accomodations) &&
      projectAccommodationsData &&
      projectAccommodationsData.length > 0
    ) {
      const accommodationIds = leadbyId.accomodations;
      const initialAccommodations = projectAccommodationsData.filter((acc) =>
        accommodationIds.includes(acc.id)
      );
      setProjectAccommodations(initialAccommodations);
    } else if (leadbyId && projectAccommodationsData) {
      // Reset if no accommodations in lead
      setProjectAccommodations([]);
    }
  }, [leadbyId, projectAccommodationsData]);

  function formatDate(dateString) {
    if (!dateString) return '';

    const date = new Date(dateString);

    if (isNaN(date?.getTime())) return '---';

    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date?.toLocaleDateString('en-GB', options); // e.g., 01 May 2025
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '₹0';
    return `₹${parseInt(amount).toLocaleString('en-IN')}`;
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'hot':
        return 'bg-orange-100 text-orange-900';
      case 'warm':
        return 'bg-amber-100 text-amber-900';
      case 'cold':
        return 'bg-blue-100 text-blue-900';
      case 'demo':
        return 'bg-indigo-100 text-indigo-900';
      case 'nurture':
        return 'bg-orange-50 text-orange-900';
      case 'future prospect':
        return 'bg-teal-100 text-teal-900';
      case 'confirmed':
        return 'bg-violet-100 text-violet-900';
      case 'closed won':
      case 'deal closed':
        return 'bg-green-100 text-green-900';
      case 'closed lost':
        return 'bg-rose-100 text-rose-900';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'deal':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'underConsideration':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const showStatusChip = leadbyId?.pstatus?.toLowerCase();
  const showTypeChip = type && type !== 'underConsideration';
  const primaryPhone = leadbyId?.user?.phone_number || '';
  const stageFlow = [
    'Lead Created',
    'Approached',
    'Phone Call Initiated',
    'Demo Scheduled',
    'Presentation',
    'Site Visited',
    'Commercials Shared',
    'Negotiation',
    'Under Consideration',
    'Partner Approval',
    'Management Approval',
    'Management Denied',
    'Application Form',
    'Allotment Letter',
    'Demand Note (Invoice)',
    'Payment Stage (Invoice)',
    'Invoice Advance',
    'Aggrement Letter',
    'Token Advance',
    'Change Request',
    'Change Request Invoice',
    'Extra Charge Claim',
    'Invoice Sent',
    'Gentle Reminder',
    'First Delay Follow-Up',
    'Second Delay Follow-Up',
    'Final Reminder / Soft Escalation',
    'Hard Escalation',
    'Legal / Final Action',
    'Payment Committed Awaiting UTR',
    'Partial Payment Received',
    'Payment Under Processing',
    'Payment Received Closed',
    'Deal Closed',
    'Deal Dropped',
    'Deal Cancelled'
  ];
  const currentStage = leadbyId?.activities?.[0]?.name || '';
  const hasLiveDeal =
    ['Pending', 'Approved'].includes(leadbyId?.deal?.status) ||
    ['Pending', 'Approved'].includes(closeDealData?.status);
  const incompleteClose =
    !hasLiveDeal &&
    (leadbyId?.pstatus === 'Closed Won' ||
      (leadbyId?.activities || []).some((activity) => activity?.name === 'Deal Closed'));
  const displayStage = incompleteClose
    ? (leadbyId?.activities || []).find((activity) => activity?.name !== 'Deal Closed')
        ?.name || currentStage
    : currentStage;
  const currentStageIndex = stageFlow.indexOf(displayStage);
  const {
    date: effectiveFollowUpDate,
    followUp: activeFollowUp
  } = getNextFollowUpForLead(leadbyId?.follow_ups);
  const lostRemark = getLeadRemark(leadbyId);
  const isClosedLostLead = leadbyId?.pstatus === 'Closed Lost';
  const latestFollowUpDate = effectiveFollowUpDate || '-';
  const hasFollowUp = Boolean(effectiveFollowUpDate || activeFollowUp);
  const hasValidFollowUpDate = (() => {
    const rawDate = effectiveFollowUpDate;
    if (!rawDate) return false;
    const parsedDate = new Date(rawDate);
    return !Number.isNaN(parsedDate.getTime());
  })();
  const nextAction =
    currentStageIndex >= 0 && currentStageIndex < stageFlow.length - 1
      ? stageFlow[currentStageIndex + 1]
      : '---';
  const latestMoveLabel = (() => {
    const q = latestQualification;
    if (!q?.fromPstatus || !q?.toPstatus || q.fromPstatus === q.toPstatus) return null;
    const when = q.movedAt ? formatDate(q.movedAt) : null;
    return when
      ? `${q.fromPstatus} → ${q.toPstatus} · ${when}`
      : `${q.fromPstatus} → ${q.toPstatus}`;
  })();
  const leadPstatus = leadbyId?.pstatus || '---';
  const aiTopSuggestion = cleanAiActionLabel(aiInsight?.nextBestActions?.[0]?.action);
  const followUpAgendaRaw = activeFollowUp?.agenda || '---';
  const followUpModeRaw = activeFollowUp?.log || '---';
  const followUpSubtitle = hasFollowUp
    ? formatDate(latestFollowUpDate) || '---'
    : 'Nothing scheduled';
  const salesJourneyGhost = (() => {
    const ghostTitle =
      aiTopSuggestion || (nextAction !== '---' ? nextAction : null);
    if (!ghostTitle) return null;
    return {
      title: ghostTitle,
      date: aiTopSuggestion ? 'Predicted' : 'Up next',
      subtitle: aiTopSuggestion ? 'Suggested next step' : 'Expected next CRM stage'
    };
  })();
  const followUpOverdue = (() => {
    const raw = effectiveFollowUpDate;
    if (!raw) return null;
    const due = new Date(raw);
    if (Number.isNaN(due.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today.getTime() - due.getTime()) / 86400000);
    if (diffDays > 0) {
      return {
        overdue: true,
        days: diffDays,
        kpiLabel: `Overdue by ${diffDays} day${diffDays === 1 ? '' : 's'}`
      };
    }
    if (diffDays === 0) {
      return { overdue: true, days: 0, kpiLabel: 'Due today' };
    }
    const daysUntil = Math.abs(diffDays);
    return {
      overdue: false,
      days: daysUntil,
      kpiLabel: `Due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`
    };
  })();
  const engagementStats = (() => {
    let calls = 0;
    let whatsapp = 0;
    let emails = 0;
    let lastTs = 0;
    for (const log of leadbyId?.sales_logs || []) {
      const name = String(log?.name || '').toLowerCase();
      if (/whatsapp|\bwa\b/.test(name)) whatsapp += 1;
      else if (/email|\bmail\b/.test(name)) emails += 1;
      else if (/call|phone|visit|meeting|site/.test(name)) calls += 1;
      const ts = new Date(log?.createdAt || log?.created_at || 0).getTime();
      if (Number.isFinite(ts) && ts > lastTs) lastTs = ts;
    }
    for (const activity of leadbyId?.activities || []) {
      const name = String(activity?.name || '').toLowerCase();
      if (/call|phone|visit|meeting|site/.test(name)) calls += 1;
      const ts = new Date(
        activity?.created_at || activity?.createdAt || activity?.updated_at || 0
      ).getTime();
      if (Number.isFinite(ts) && ts > lastTs) lastTs = ts;
    }
    return { calls, whatsapp, emails, lastTs };
  })();
  const lastActivityLabel = (() => {
    if (!engagementStats.lastTs) return '---';
    const diffMs = Date.now() - engagementStats.lastTs;
    const hours = Math.floor(diffMs / 3600000);
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  })();
  const linkedInLocationParts = (() => {
    const location = String(leadbyId?.location || '').trim();
    if (!location) return { city: null, state: null };
    const parts = location
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length === 1) return { city: parts[0], state: null };
    const countryLike =
      /^(india|usa|united states|u\.?s\.?a\.?|uae|united arab emirates|singapore|uk|united kingdom|canada|australia)$/i;
    if (parts.length >= 3 && countryLike.test(parts[parts.length - 1])) {
      return { city: parts[0], state: parts[1] };
    }
    if (parts.length === 2 && countryLike.test(parts[1])) {
      return { city: parts[0], state: null };
    }
    return { city: parts[0], state: parts[1] };
  })();
  const pickDisplayValue = (...values) => {
    for (const value of values) {
      if (value == null) continue;
      const text = String(value).trim();
      if (text && text !== '---' && text !== '-') return text;
    }
    return '---';
  };
  const linkedInProfileUrl = (() => {
    const raw = String(leadbyId?.linkedin_profile_url || '').trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    if (/linkedin\.com/i.test(raw)) return `https://${raw.replace(/^\/+/, '')}`;
    return null;
  })();
  const basicDetails = {
    name: leadbyId?.user?.name || '---',
    phone: leadbyId?.user?.phone_number || '---',
    alternatePhone: leadbyId?.alternate_phone_number || '---',
    email: pickDisplayValue(
      leadbyId?.user?.email,
      leadbyId?.official_email,
      leadbyId?.personal_email
    ),
    city: pickDisplayValue(leadbyId?.address?.city, linkedInLocationParts.city),
    state: pickDisplayValue(leadbyId?.address?.state, linkedInLocationParts.state),
    source: getLeadSourceDisplay(leadbyId),
    occupation: pickDisplayValue(leadbyId?.occupation),
    designation: pickDisplayValue(leadbyId?.designation),
    companyName: pickDisplayValue(leadbyId?.company_name),
    referenceName: leadbyId?.reference_name || leadbyId?.reference || '---'
  };
  const leadNameDisplay =
    linkedInProfileUrl && basicDetails.name && basicDetails.name !== '---' ? (
      <a
        href={linkedInProfileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-inherit no-underline hover:text-[#0A66C2] hover:underline transition-colors"
        title="Open LinkedIn profile">
        {basicDetails.name}
      </a>
    ) : (
      basicDetails.name
    );
  const requirementDetails = {
    project: leadbyId?.projects?.[0]?.name || '---',
    type:
      leadbyId?.accomodation?.[0]?.name ||
      (Array.isArray(leadbyId?.interestedAccomodationType) &&
      leadbyId.interestedAccomodationType.length > 0
        ? leadbyId.interestedAccomodationType.join(', ')
        : null) ||
      projectAccommodations?.[0]?.name ||
      '---',
    preferredUnit: getLeadAssociatedUnitLabel(leadbyId),
    budget: leadbyId?.expectedBudgetRange ? `${leadbyId.expectedBudgetRange} Lakhs` : '---',
    timeline: formatDate(leadbyId?.expected_clouser_date) || '---',
    interestedPlotArea:
      Array.isArray(leadbyId?.plotArea) && leadbyId.plotArea.length > 0
        ? leadbyId.plotArea.join(', ')
        : '---',
    interestedAccommodationType:
      Array.isArray(leadbyId?.interestedAccomodationType) &&
      leadbyId.interestedAccomodationType.length > 0
        ? leadbyId.interestedAccomodationType.join(', ')
        : '---'
  };
  const historyLogsTabs = combineHistoryAndLogsTabs
    ? [STAGE_HISTORY_AND_LOGS_TAB]
    : ['Logs', 'Stage History'];
  const historyTabTarget = historyLogsTabs[0];
  const mapActivityTimeline = (activity) => {
    const rawDate =
      activity?.created_at ||
      activity?.createdAt ||
      activity?.date ||
      activity?.updated_at ||
      activity?.updatedAt;
    return {
      title: activity?.name || activity?.title || 'Activity updated',
      subtitle: activity?.description || '',
      date: formatDate(rawDate) || '---',
      ts: rawDate ? new Date(rawDate).getTime() : 0
    };
  };
  const mapSalesLogTimeline = (log) => {
    const rawDate = log?.createdAt || log?.created_at || log?.updatedAt || log?.updated_at;
    const title = log?.name || 'Sales log updated';
    const description = log?.description || '';
    const transferredBy =
      log?.createdBy?.name || log?.created_by?.name || log?.createdByName || '';
    const subtitleParts = [];
    if (description?.trim() && description.trim() !== title.trim()) {
      subtitleParts.push(description.trim());
    }
    if (
      transferredBy &&
      !String(title).toLowerCase().includes(`by ${String(transferredBy).toLowerCase()}`) &&
      !String(description).toLowerCase().includes(`by ${String(transferredBy).toLowerCase()}`)
    ) {
      subtitleParts.push(`By ${transferredBy}`);
    }
    return {
      title,
      subtitle: subtitleParts.join(' · '),
      date: formatDate(rawDate) || '---',
      ts: rawDate ? new Date(rawDate).getTime() : 0
    };
  };
  const salesJourneyAll = ((leadbyId?.activities || []).map(mapActivityTimeline) || []).sort(
    (a, b) => b.ts - a.ts
  );
  const salesLogAll = ((leadbyId?.sales_logs || []).map(mapSalesLogTimeline) || []).sort(
    (a, b) => b.ts - a.ts
  );
  const salesJourneyItems = salesJourneyAll.slice(0, 6);
  const salesLogItems = salesLogAll.slice(0, 6);
  const salesJourneyPreview = salesJourneyAll.slice(0, 2);
  const salesLogPreview = salesLogAll.slice(0, 1);
  const contactPreviewItems = buildPreviewItems([
    { label: 'Name', value: basicDetails.name },
    { label: 'Phone', value: basicDetails.phone },
    { label: 'Email', value: basicDetails.email },
    { label: 'Source', value: basicDetails.source }
  ]).slice(0, 4);
  const requirementPreviewItems = buildPreviewItems([
    { label: 'Project', value: requirementDetails.project },
    { label: 'Configuration', value: requirementDetails.type },
    { label: 'Budget', value: requirementDetails.budget },
    { label: 'Possession', value: requirementDetails.timeline }
  ]).slice(0, 4);
  const hasEngagementData =
    engagementStats.calls + engagementStats.whatsapp + engagementStats.emails > 0 ||
    engagementStats.lastTs > 0;
  const budgetKpiLabel = (() => {
    const qualBudget =
      qualificationProfileData?.cards?.find((c) => c.fieldId === 'confirmed_budget_range')
        ?.value ||
      qualificationProfileData?.cards?.find((c) => c.fieldId === 'approximate_budget')?.value;
    if (qualBudget) return qualBudget;
    if (leadbyId?.expectedBudgetRange) return `${leadbyId.expectedBudgetRange} Lakhs`;
    if (leadbyId?.exptceted_budget) return `${leadbyId.exptceted_budget} Lakhs`;
    return 'Not set';
  })();
  const budgetKpiSubLabel =
    budgetKpiLabel === 'Not set' ? 'Awaiting qualification' : 'From lead / qualification';
  // Show the WhatsApp chat tab only when the lead has a phone number to converse on.
  const whatsappTabs = leadbyId?.user?.phone_number ? ['WhatsApp'] : [];
  const headerTabs =
    type === 'underConsideration'
      ? ['Overview', ...whatsappTabs, 'Pricing Details', ...historyLogsTabs]
      : type === 'deal'
        ? ['Overview', ...whatsappTabs, 'Pricing Details', ...historyLogsTabs, 'Post Sales']
        : ['Overview', ...whatsappTabs, ...historyLogsTabs];

  // Reset Follow Up Form
  const resetFollowUpForm = () => {
    setAgenda('');
    setLog('');
    setFollowUpDate('');
    setStage('');
    setOtherLog('');
    setUploadedImage(null);
    setProjectAccommodations([]);
    setValue('followUpStage', '');
    setSelectedStage('');
    setWantStageQualification(false);
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const token = Cookies.get('token');
      if (!token) throw new Error('No token found');

      const formData = new FormData();
      formData.append('files', file);

      const res = await axios.post('https://application.housingram.com/api/s3/upload', formData, {
        headers: {
          authorization: token,
          'Content-Type': 'multipart/form-data'
        }
      });

      const uploaded = res.data.data.files[0];
      setUploadedImage({
        name: uploaded.originalName,
        url: uploaded.presignedUrl
      });
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  // Handle Complete Follow up
  const openQualificationIfRequired = async (targetStage, onQualified, optedIn = false) => {
    if (!optedIn) {
      await onQualified(null);
      return false;
    }
    try {
      const required = await getStageQualificationRequired(ID, targetStage);
      if (required?.required && (required?.mandatory?.length ?? 0) > 0) {
        setPendingQualAction({
          transition: required.transition,
          targetStage,
          onQualified
        });
        setQualModalOpen(true);
        return true;
      }
      if (optedIn && required?.transition && !(required?.mandatory?.length > 0)) {
        toast.error(
          'No qualification questions configured. Add them from Leads → Qualification questions.'
        );
      }
      await onQualified(null);
      return false;
    } catch (error) {
      // Additive rollout: if qualification API is unavailable, do not block legacy stage moves
      if (error?.response?.status === 404 || error?.response?.status >= 500) {
        console.warn('Stage qualification API unavailable — proceeding without gate', error);
        toast.error('Qualification service unavailable — stage updated without gate (deploy backend)');
        await onQualified(null);
        return false;
      }
      toast.error(error?.response?.data?.message || 'Could not verify stage qualification');
      throw error;
    }
  };

  const handleQualificationSubmit = async (stageQualification) => {
    if (!pendingQualAction?.onQualified) return;
    try {
      setQualSubmitting(true);
      await pendingQualAction.onQualified(stageQualification);
      setQualModalOpen(false);
      setPendingQualAction(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Stage update failed');
    } finally {
      setQualSubmitting(false);
    }
  };

  const executeFollowUpComplete = async (stageQualification) => {
    const existingFollowUpId = activeFollowUp?.id;
    const needsNextFollowUp = requiresNextFollowUpFields({ stage, log });
    const noteText = (agenda || '').trim();
    const payload = {
      agenda: noteText,
      nextFollowUpDate: needsNextFollowUp ? followUpDate : null,
      log: log === 'Other' ? otherLog : log,
      lead_id: ID,
      // Not Interested / terminal stages: do not leave an open next follow-up.
      status: needsNextFollowUp && !existingFollowUpId ? 'Active' : 'Inactive',
      name: stage,
      attachments: uploadedImage ? [{ name: uploadedImage.name, url: uploadedImage.url }] : [],
      // Closure reason belongs on the stage activity so Stage History shows why.
      ...(!needsNextFollowUp && noteText ? { description: noteText } : {}),
      ...(stageQualification ? { stageQualification } : {})
    };

    setIsSubmitting(true);
    if (existingFollowUpId) {
      await updateFollowUp(existingFollowUpId, payload);
    } else {
      await addFollowUp(payload);
    }

    if (stage === 'Commercials Shared' || stage === 'Negotiation') {
      try {
        const leadPayload = {
          accomodations: projectAccommodations.map((item) => item.id || item)
        };
        await updateLead(ID, leadPayload);
      } catch (leadError) {
        console.error('Failed to update lead project accommodation:', leadError);
      }
    }

    toast.success('Follow Up  successfully');
    await refetchLeadbyId();
    invalidateLeadAiCaches();
    setShowFollowUpModal(false);
    resetFollowUpForm();

    if (selectedStage && selectedStage === 'Under Consideration') {
      setIsUnderConsiderationModalOpen(true);
    }
    if (selectedStage && selectedStage === 'Deal Closed') {
      setIsCloseDealModalOpen(true);
    }
    setIsSubmitting(false);
  };

  const handleCompleteFollowup = async () => {
    if (!stage) {
      toast.error('Select Stage');
      return;
    }

    if (!log) {
      toast.error('Select/Enter log');
      return;
    }

    if (log === 'Other' && !otherLog) {
      toast.error('Enter log');
      return;
    }

    const needsNextFollowUp = requiresNextFollowUpFields({ stage, log });

    if (needsNextFollowUp && !followUpDate) {
      toast.error(upcomingContactDateRequiredToast(stage));
      return;
    }

    if (
      isUpcomingContactStage(stage) &&
      !isValidUpcomingContactDate(followUpDate)
    ) {
      toast.error(upcomingContactPastDateToast(stage));
      return;
    }

    if (needsNextFollowUp && !agenda) {
      toast.error(upcomingAgendaRequiredToast(stage));
      return;
    }

    if (requiresClosureNote({ stage, log }) && !agenda?.trim()) {
      toast.error(`Enter ${closureNoteLabel({ stage, log }).toLowerCase()}`);
      return;
    }

    // Validate accommodation for specific stages
    if (
      (stage === 'Commercials Shared' || stage === 'Negotiation') &&
      projectAccommodations.length === 0
    ) {
      toast.error('Please select at least one project accommodation');
      return;
    }

    try {
      await openQualificationIfRequired(stage, executeFollowUpComplete, wantStageQualification);
    } catch (error) {
      console.error('Failed to update followup:', error);
    }
  };

  const handleOpenFollowUpModal = () => {
    const currentStage = leadbyId?.activities?.[0]?.name;
    if (currentStage && stageOptions?.find((item) => item.id === currentStage)) {
      setStage(currentStage);
      setValue('followUpStage', currentStage);
      setSelectedStage(currentStage);
    }

    // Prefill will be handled by useEffect when projectAccommodationsData loads
    setShowFollowUpModal(true);
  };

  const handleCloseDeal = (data) => {
    createClosedDealMutation(data);
  };

  const { mutate: createClosedDealMutation } = useMutation({
    mutationFn: (data) => createClosedDeal(data),
    onSuccess: () => {
      toast.success('Deal closed successfully');
      setIsCloseDealModalOpen(false);
      refetchLeadbyId();
      invalidateLeadAiCaches();
      // Closed Won stays on leads; process continues in Deals / post-sales hub.
      navigate(`/builder/deals/${ID}`, { state: { status: 'Closed Won' } });
    },
    onError: (error) => {
      toast.error(error.response.data.message || error?.message || 'Failed to close deal');
    }
  });

  const handleUnderConsideration = (data) => {
    createUnderConsiderationMutation(data);
  };

  const { mutate: createUnderConsiderationMutation } = useMutation({
    mutationFn: (data) => createUnderConsideration(data),
    onSuccess: () => {
      toast.success('Under Consideration created successfully');
      setIsUnderConsiderationModalOpen(false);
      refetchLeadbyId();
      invalidateLeadAiCaches();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to create under consideration'
      );
    }
  });

  const executeStageUpdate = async (stageQualification) => {
    setIsUpdatingStage(true);
    try {
      if (isUpcomingContactStage(stageToUpdate)) {
        const existingFollowUpId = activeFollowUp?.id;
        const payload = {
          agenda,
          nextFollowUpDate: followUpDate,
          log: isDemoScheduledStage(stageToUpdate)
            ? 'Demo Scheduled — upcoming demo'
            : 'Future Prospect — contact scheduled',
          lead_id: ID,
          status: existingFollowUpId ? 'Inactive' : 'Active',
          name: isDemoScheduledStage(stageToUpdate)
            ? 'Demo Scheduled'
            : 'Future Prospect',
          attachments: [],
          ...(stageQualification ? { stageQualification } : {})
        };
        if (existingFollowUpId) {
          await updateFollowUp(existingFollowUpId, payload);
        } else {
          await addFollowUp(payload);
        }
      } else {
        const payload = {
          name: stageToUpdate,
          lead_id: ID,
          ...(stageQualification ? { stageQualification } : {})
        };
        await updateStage(payload);
      }

      if (stageToUpdate === 'Commercials Shared' || stageToUpdate === 'Negotiation') {
        try {
          const leadPayload = {
            accomodations: projectAccommodations.map((item) => item.id || item)
          };
          await updateLead(ID, leadPayload);
        } catch (leadError) {
          console.error('Failed to update lead project accommodation:', leadError);
        }
      }

      toast.success(
        isDemoScheduledStage(stageToUpdate)
          ? 'Moved to Demo Scheduled'
          : isFutureProspectStage(stageToUpdate)
            ? 'Moved to Future Prospect'
            : 'Stage updated successfully'
      );
      setShowStageModal(false);
      setStageToUpdate('');
      setProjectAccommodations([]);
      setFollowUpDate('');
      setAgenda('');
      await refetchLeadbyId();
      invalidateLeadAiCaches();
    } finally {
      setIsUpdatingStage(false);
    }
  };

  // Handle stage update
  const handleUpdateStage = async () => {
    if (!stageToUpdate) {
      toast.error('Please select a stage');
      return;
    }

    if (stageToUpdate === currentStage && !(incompleteClose && stageToUpdate === 'Deal Closed')) {
      toast.error('This is already the current stage — pick a different one.');
      return;
    }

    if (
      (stageToUpdate === 'Commercials Shared' || stageToUpdate === 'Negotiation') &&
      projectAccommodations.length === 0
    ) {
      toast.error('Please select at least one project accommodation');
      return;
    }

    if (isUpcomingContactStage(stageToUpdate)) {
      if (!followUpDate) {
        toast.error(upcomingContactDateRequiredToast(stageToUpdate));
        return;
      }
      if (!isValidUpcomingContactDate(followUpDate)) {
        toast.error(upcomingContactPastDateToast(stageToUpdate));
        return;
      }
      if (!agenda) {
        toast.error(upcomingAgendaRequiredToast(stageToUpdate));
        return;
      }
    }

    if (stageToUpdate === 'Deal Closed') {
      setShowStageModal(false);
      setIsCloseDealModalOpen(true);
      return;
    }

    if (stageToUpdate === 'Under Consideration') {
      setShowStageModal(false);
      setIsUnderConsiderationModalOpen(true);
      return;
    }

    try {
      await openQualificationIfRequired(stageToUpdate, executeStageUpdate, wantStageQualification);
    } catch (error) {
      console.error('Failed to update stage:', error);
      setIsUpdatingStage(false);
    }
  };

  const handleMoveToNurtureFromBanner = () => {
    setStageToUpdate('Paused / Nurture');
    setPendingQualAction({
      transition: 'to_nurture',
      targetStage: 'Paused / Nurture',
      onQualified: async (stageQualification) => {
        setIsUpdatingStage(true);
        try {
          await updateStage({
            name: 'Paused / Nurture',
            lead_id: ID,
            stageQualification
          });
          toast.success('Lead moved to Paused / Nurture');
          await refetchLeadbyId();
          invalidateLeadAiCaches();
        } finally {
          setIsUpdatingStage(false);
        }
      }
    });
    setQualModalOpen(true);
  };

  const handleUpdateFollowUpDate = async () => {
    if (!selectedFollowUp) return;
    if (!followUpDate) {
      toast.error('Please select a date');
      return;
    }

    try {
      setIsUpdatingFollowUp(true);
      const payload = {
        nextFollowUpDate: new Date(followUpDate).toISOString()
        // agenda: selectedFollowUp.agenda // Keep existing agenda
      };

      await updateFollowUpDate(selectedFollowUp.id, payload);
      toast.success('Follow up date updated successfully');
      setShowUpdateFollowUpModal(false);
      setSelectedFollowUp(null);
      setFollowUpDate('');
      refetchLeadbyId();
      invalidateLeadAiCaches();
    } catch (error) {
      console.error('Failed to update follow up date:', error);
      toast.error(error?.response?.data?.message || 'Failed to update follow up date');
    } finally {
      setIsUpdatingFollowUp(false);
    }
  };

  const handleEditLeadSubmit = async (payload) => {
    try {
      setIsUpdatingLeadDetails(true);
      await updateLead(ID, payload);
      toast.success('Lead updated successfully');
      setIsEditLeadModalOpen(false);
      await refetchLeadbyId();
      invalidateLeadAiCaches();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update lead');
    } finally {
      setIsUpdatingLeadDetails(false);
    }
  };

  const handleContactProfileSubmit = async (payload) => {
    try {
      setIsUpdatingContactProfile(true);
      await updateLead(ID, payload);
      toast.success('Contact profile updated');
      setIsEditContactProfileOpen(false);
      await refetchLeadbyId();
      invalidateLeadAiCaches();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to update contact profile'
      );
    } finally {
      setIsUpdatingContactProfile(false);
    }
  };

  const closeAddNoteModal = () => {
    if (isAddingNote) return;
    setShowAddNoteModal(false);
    setStandaloneNote('');
  };

  const handleAddStandaloneNote = async () => {
    const noteText = standaloneNote.trim();
    if (!noteText) {
      toast.error('Enter a note');
      return;
    }
    if (!ID) {
      toast.error('Lead not found');
      return;
    }

    try {
      setIsAddingNote(true);
      const salesUserId = Number(userId);
      await addLog({
        name: 'Note',
        description: noteText,
        sales_user_id: Number.isFinite(salesUserId) ? salesUserId : undefined,
        lead_id: Number(ID),
        file: ''
      });
      toast.success('Note added');
      setShowAddNoteModal(false);
      setStandaloneNote('');
      await refetchLeadbyId();
      queryClient.invalidateQueries({ queryKey: ['getLogsByID', Number(ID)] });
      mixpanel.track('Lead Standalone Note Added', {
        user_id: userId,
        lead_id: ID,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to add note');
    } finally {
      setIsAddingNote(false);
    }
  };

  const { mutate: cloneLeadMutation, isPending: isCloningLead } = useMutation({
    mutationFn: (payload) => cloneLead(ID, payload),
    onMutate: (data) => {
      mixpanel.track('Duplicate Lead Started', {
        user_id: userId,
        lead_id: ID,
        project_ids: data.project_ids,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: (result, variables) => {
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Duplicate Lead Failed', {
          user_id: userId,
          error_message:
            result?.response?.data?.message || result?.message || 'Failed to duplicate lead',
          lead_id: ID,
          project_ids: variables.project_ids,
          timestamp: new Date().toISOString()
        });
        toast.error(
          result?.response?.data?.message || result?.message || 'Failed to duplicate lead'
        );
        return;
      }

      mixpanel.track('Duplicate Lead Success', {
        user_id: userId,
        lead_id: ID,
        project_ids: variables.project_ids,
        timestamp: new Date().toISOString()
      });

      toast.success('Lead cloned successfully');
      setIsCloneLeadModalOpen(false);
      setSelectedCloneProjects([]);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (error, variables) => {
      mixpanel.track('Duplicate Lead Failed', {
        user_id: userId,
        error_message:
          error?.response?.data?.message || error?.message || 'Failed to duplicate lead',
        lead_id: ID,
        project_ids: variables?.project_ids,
        timestamp: new Date().toISOString()
      });
      toast.error(error?.response?.data?.message || error?.message || 'Failed to duplicate lead');
    }
  });

  const handleOpenCloneLeadModal = () => {
    mixpanel.track('Duplicate Lead Modal Opened', {
      user_id: userId,
      lead_id: ID,
      lead_name: leadbyId?.user?.name,
      timestamp: new Date().toISOString()
    });
    setSelectedCloneProjects([]);
    setIsCloneLeadModalOpen(true);
  };

  const handleSubmitCloneLead = () => {
    if (selectedCloneProjects.length === 0) {
      toast.error('Please select at least one project');
      return;
    }

    cloneLeadMutation({
      project_ids: selectedCloneProjects.map((project) => parseInt(project.id, 10))
    });
  };

  const cloneProjectOptions =
    projects
      ?.filter((project) => {
        const currentProjectId = leadbyId?.interested_projects?.[0];
        return currentProjectId ? project.id !== currentProjectId : true;
      })
      ?.map((project) => ({
        id: project.id.toString(),
        name: project.name
      })) || [];

  const navigateToLeadsList = () => {
    const projectID = leadbyId?.interested_projects?.[0] ?? null;
    navigate('/builder/sales/', {
      state: {
        secondaryTab: 'Leads',
        ...(projectID ? { projectID } : {})
      }
    });
  };

  const { mutate: checkDeleteLead } = useMutation({
    mutationFn: () => handleCheckLeadDelete(ID),
    onMutate: () => setIsDeletingLead(true),
    onSuccess: (result) => {
      setIsDeletingLead(false);
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        toast.error(
          result?.response?.data?.message || result?.message || 'Error checking dependencies'
        );
        return;
      }

      const leadName = leadbyId?.user?.name || 'Lead';
      if (result.canDelete) {
        setDeleteResData({ name: leadName });
        setCanDelete('yes');
      } else {
        setDeleteResData({ ...result, name: leadName });
        setCanDelete('no');
      }
    },
    onError: (error) => {
      setIsDeletingLead(false);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error checking dependencies'
      );
    }
  });

  const { mutate: deleteLeadMutation } = useMutation({
    mutationFn: (payload) => deleteLead(payload),
    onMutate: () => setIsDeletingLead(true),
    onSuccess: async (result) => {
      setIsDeletingLead(false);
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        toast.error(result?.response?.data?.message || result?.message || 'Error deleting lead');
        return;
      }

      toast.success('Lead deleted successfully');
      setCanDelete(null);
      setDeleteResData(null);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      navigateToLeadsList();
    },
    onError: (error) => {
      setIsDeletingLead(false);
      toast.error(error?.response?.data?.message || error?.message || 'Error deleting lead');
    }
  });

  const handleDeleteLeadClick = () => {
    mixpanel.track('Delete Lead Clicked', {
      user_id: userId,
      lead_id: ID,
      lead_name: leadbyId?.user?.name,
      timestamp: new Date().toISOString()
    });
    checkDeleteLead();
  };

  const handleConfirmDeleteLead = () => {
    deleteLeadMutation({ ids: [parseInt(ID, 10)] });
  };

  return (
    <div className="overflow-y-auto p-4 bg-gray-50 w-full">
      <div className="mx-auto max-w-[1500px] flex flex-col gap-2">
        {/* Header Section */}
        <div className="px-2 md:px-4 mb-2">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center text-teal-700 font-semibold text-sm flex-shrink-0">
                {leadbyId?.user?.name?.charAt(0)?.toUpperCase() || 'L'}
              </div>
              <p className="font-bold text-gray-900 text-2xl md:text-[30px] leading-tight">
                {leadNameDisplay}
              </p>
              <span className="text-gray-500 text-base font-medium">#{ID || '---'}</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-800 text-sm leading-none font-semibold">
                <MapPin size={12} />
                {displayStage || 'Stage Pending'}
              </span>
              {showStatusChip && (
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm leading-none capitalize font-semibold ${getStatusColor(leadbyId?.pstatus)}`}>
                  <Flame size={12} />
                  {leadbyId?.pstatus || '---'}
                </span>
              )}
              {!leadbyId?.activities?.some(
                (activity) => activity?.name === 'Management Approval'
              ) && (
                <button
                  type="button"
                  title="Move Stage"
                  aria-label="Move Stage"
                  onClick={() => {
                    setStageToUpdate('');
                    setShowStageModal(true);
                  }}
                  className="inline-flex items-center justify-center p-2 rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-800 transition-colors">
                  <MoveRight className="w-4 h-4" strokeWidth={2.25} />
                </button>
              )}
              <button
                type="button"
                title="Edit Lead"
                aria-label="Edit Lead"
                onClick={() => setIsEditLeadModalOpen(true)}
                className="inline-flex items-center justify-center p-2 rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-800 transition-colors">
                <Pencil className="w-4 h-4" strokeWidth={2.25} />
              </button>
              {Number(leadbyId?.pre_lead_id || leadbyId?.preLead?.id) > 0 && (
                <button
                  type="button"
                  title="Prelead Form"
                  aria-label="View Prelead Form"
                  onClick={() => setShowPreleadFormModal(true)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-md border border-gray-300 bg-white text-gray-700 text-sm font-medium shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 transition-colors">
                  <ClipboardList className="w-4 h-4" strokeWidth={2.25} />
                  Prelead Form
                </button>
              )}
              {type !== 'deal' && (
                <>
                  <button
                    type="button"
                    title="Duplicate Lead"
                    aria-label="Duplicate Lead"
                    onClick={handleOpenCloneLeadModal}
                    className="inline-flex items-center justify-center p-2 rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-800 transition-colors">
                    <Copy className="w-4 h-4" strokeWidth={2.25} />
                  </button>
                  {canDeleteLeads && (
                    <button
                      type="button"
                      title="Delete Lead"
                      aria-label="Delete Lead"
                      onClick={handleDeleteLeadClick}
                      disabled={isDeletingLead}
                      className="inline-flex items-center justify-center p-2 rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-800 transition-colors disabled:opacity-50 disabled:pointer-events-none">
                      <Trash2 className="w-4 h-4" strokeWidth={2.25} />
                    </button>
                  )}
                </>
              )}
              {showTypeChip && (
                <span
                  className={`px-3 py-1.5 rounded-md border text-sm leading-none capitalize font-semibold ${getTypeBadgeColor(type)}`}>
                  {type}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-base text-gray-700 pl-0.5">
              {primaryPhone && (
                <span className="flex items-center gap-2">
                  <span className="text-gray-400">•</span>
                  <span className="font-medium">{primaryPhone}</span>
                </span>
              )}
              {getLeadSourceDisplay(leadbyId) !== '---' && (
                <span className="flex items-center gap-2">
                  <span className="text-gray-400">•</span>
                  <span className="font-medium">{getLeadSourceDisplay(leadbyId)}</span>
                </span>
              )}
              {leadbyId?.projects?.[0]?.name && (
                <span className="flex items-center gap-2">
                  <span className="text-gray-400">•</span>
                  <span className="font-medium">{leadbyId.projects[0].name}</span>
                </span>
              )}
              {requirementDetails.preferredUnit !== '---' && (
                <span className="flex items-center gap-2">
                  <span className="text-gray-400">•</span>
                  <span className="font-medium">Unit: {requirementDetails.preferredUnit}</span>
                </span>
              )}
              {leadbyId?.expectedBudgetRange && (
                <span className="flex items-center gap-2">
                  <span className="text-gray-400">•</span>
                  <span className="font-medium">{`${leadbyId.expectedBudgetRange} Lakhs`}</span>
                </span>
              )}
              {hasValidFollowUpDate && (
                <span className="flex items-center gap-2">
                  <span className="text-gray-400">•</span>
                  <span className="font-medium">
                    Follow up: {formatDate(effectiveFollowUpDate)}
                  </span>
                </span>
              )}
              {leadbyId?.sales?.name && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-100 text-violet-800 text-sm">
                  <User size={13} />
                  <span className="font-semibold">Sales Person:</span>
                  <span className="font-medium">{leadbyId.sales.name}</span>
                </span>
              )}
              {basicDetails.companyName && basicDetails.companyName !== '---' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-800 text-sm">
                  <Building2 size={13} />
                  <span className="font-medium">{basicDetails.companyName}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {incompleteClose && type !== 'deal' && (
          <div className="mx-2 md:mx-4 mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <span>Close Deal was left unfinished. This lead stays on {displayStage || 'Confirmed'} until you submit the form.</span>
            <button
              type="button"
              className="rounded-md bg-amber-700 px-3 py-1 text-white hover:bg-amber-800"
              onClick={() => setIsCloseDealModalOpen(true)}>
              Resume Close Deal
            </button>
          </div>
        )}

        <div className="px-2 md:px-4 mb-2 border-b border-gray-200">
          <div className="flex items-center gap-5 overflow-x-auto whitespace-nowrap">
            {headerTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => onHeaderTabChange?.(tab)}
                className={`text-base pb-2.5 ${
                  tab === activeHeaderTab
                    ? 'text-teal-700 border-b-2 border-teal-700 font-semibold'
                    : 'text-gray-700 font-medium hover:text-gray-900'
                }`}>
                {tab}
              </button>
            ))}
          </div>
        </div>

        {activeHeaderTab === 'Overview' && (
          <>
            <div className="px-2 md:px-4 mb-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              {type === 'deal' ? (
                <>
                  <div className="rounded-xl border border-teal-200 bg-teal-50 p-4 min-h-[5.5rem] flex flex-col justify-center">
                    <p className="text-xs font-semibold uppercase tracking-wide text-teal-800/80">
                      Deal status
                    </p>
                    <p className="text-lg font-semibold mt-1 text-teal-950">Closed Won</p>
                    <p className="text-sm mt-1 truncate text-teal-900/80">
                      {closeDealData?.unit?.unitName ||
                        leadbyId?.deal?.unit?.unitName ||
                        'Unit assigned'}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-4 min-h-[5.5rem] flex flex-col justify-center">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Applicants
                    </p>
                    <p className="text-lg text-gray-900 font-semibold mt-1">
                      {dealApplicants?.length ? `${dealApplicants.length} on file` : 'None yet'}
                    </p>
                    <p className="text-sm mt-1 text-gray-500">
                      {dealApplicants?.length
                        ? 'Open Post Sales to manage'
                        : 'Add buyers in Post Sales'}
                    </p>
                  </div>
                  <div
                    className={`rounded-xl border p-4 min-h-[5.5rem] flex flex-col justify-center ${
                      postSalesKyc?.complete
                        ? 'border-emerald-200 bg-emerald-50'
                        : 'border-amber-200 bg-amber-50'
                    }`}>
                    <p className="text-xs font-semibold uppercase tracking-wide opacity-80">
                      KYC documents
                    </p>
                    <p className="text-lg font-semibold mt-1">
                      {postSalesKyc?.complete ? 'Complete' : 'Incomplete'}
                    </p>
                    <p className="text-sm mt-1 truncate opacity-90">
                      {postSalesKyc?.complete
                        ? 'PAN + ID on file'
                        : postSalesKyc?.missing?.join(' · ') || 'Upload in Post Sales'}
                    </p>
                  </div>
                </>
              ) : (
                <>
              <div className={`rounded-xl border p-4 min-h-[5.5rem] flex flex-col justify-center ${getStatusColor(leadPstatus)} border-transparent`}>
                <p className="text-xs font-semibold uppercase tracking-wide opacity-80">Lead status</p>
                <p className="text-lg font-semibold mt-1 capitalize">{leadPstatus}</p>
                <p className="text-sm mt-1 truncate opacity-90" title={latestMoveLabel || currentStage}>
                  {latestMoveLabel || currentStage || '---'}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 min-h-[5.5rem] flex flex-col justify-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Next follow-up</p>
                <p className="text-lg text-gray-900 font-semibold mt-1">
                  {formatDate(latestFollowUpDate) || '---'}
                </p>
                <p
                  className={`text-sm mt-1 ${
                    followUpOverdue?.overdue ? 'text-amber-700 font-medium' : 'text-gray-500'
                  }`}>
                  {hasValidFollowUpDate
                    ? followUpOverdue?.kpiLabel || 'Scheduled'
                    : 'Nothing scheduled'}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 min-h-[5.5rem] flex flex-col justify-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Budget</p>
                <p className="text-lg text-gray-900 font-semibold mt-1">{budgetKpiLabel}</p>
                <p className="text-sm mt-1 text-gray-500">{budgetKpiSubLabel}</p>
              </div>
                </>
              )}
            </div>

            <LeadTemperatureMovementCard
              movement={aiInsight?.temperatureMovement}
              isLoading={aiInsightLoading && !aiInsight?.temperatureMovement}
              leadName={leadbyId?.user?.name || basicDetails?.name}
            />

            <div className="mb-3 min-w-0">
              <AiInsightsCard
                leadId={leadIdNum}
                onSent={() => {
                  invalidateLeadAiCaches();
                  refetchLeadbyId();
                }}
                currentStage={leadbyId?.activities?.[0]?.name || 'Approached'}
                hasActiveFollowUp={hasValidFollowUpDate}
                followUpId={activeFollowUp?.id}
                followUpModalOpen={aiFollowUpOpen}
                onFollowUpModalChange={setAiFollowUpOpen}
              />
            </div>

            <div className={`grid grid-cols-1 ${OVERVIEW_COLUMN_GRID} gap-3 items-start`}>
              <div className="space-y-3 min-w-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
              <ExpandableProfileSection
                title="Contact profile"
                subtitle={`${basicDetails.name} · ${basicDetails.phone}`}
                icon={User}
                preview={
                  contactPreviewItems.length ? (
                    <ProfilePreviewGrid items={contactPreviewItems} />
                  ) : null
                }
                footerAction={
                  canEditContactProfile ? (
                    <button
                      type="button"
                      onClick={() => setIsEditContactProfileOpen(true)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 hover:text-teal-800">
                      <Pencil className="h-3.5 w-3.5" strokeWidth={2.25} />
                      Edit contact profile
                    </button>
                  ) : null
                }>
                <div className="grid grid-cols-1 gap-y-0">
                <ProfileRow
                  label="Name"
                  value={leadNameDisplay}
                  title={basicDetails.name !== '---' ? basicDetails.name : undefined}
                  showWhenEmpty
                />
                <ProfileRow label="Phone" value={basicDetails.phone} highlight showWhenEmpty />
                <ProfileRow label="Alternate phone" value={basicDetails.alternatePhone} showWhenEmpty />
                <ProfileRow label="Email" value={basicDetails.email} showWhenEmpty />
                <ProfileRow label="City" value={basicDetails.city} showWhenEmpty />
                <ProfileRow label="State" value={basicDetails.state} showWhenEmpty />
                <ProfileRow label="Occupation" value={basicDetails.occupation} showWhenEmpty />
                <ProfileRow label="Designation" value={basicDetails.designation} showWhenEmpty />
                <ProfileRow label="Company" value={basicDetails.companyName} showWhenEmpty />
                <ProfileRow label="Referred by" value={basicDetails.referenceName} showWhenEmpty />
                <ProfileRow label="Source" value={basicDetails.source} showWhenEmpty />
                </div>
              </ExpandableProfileSection>

              <ExpandableProfileSection
                title="Requirement"
                subtitle={requirementDetails.project || 'Requirement details'}
                icon={MapPin}
                preview={
                  requirementPreviewItems.length ? (
                    <ProfilePreviewGrid items={requirementPreviewItems} />
                  ) : null
                }>
                <div className="grid grid-cols-1 gap-y-0">
                  <ProfileRow label="Project" value={requirementDetails.project} showWhenEmpty />
                  <ProfileRow label="Type" value={requirementDetails.type} showWhenEmpty />
                  <ProfileRow label="Associated unit" value={requirementDetails.preferredUnit} showWhenEmpty />
                  <ProfileRow label="Budget" value={requirementDetails.budget} showWhenEmpty />
                  <ProfileRow label="Plot area" value={requirementDetails.interestedPlotArea} showWhenEmpty />
                  <ProfileRow
                    label="Accommodation"
                    value={requirementDetails.interestedAccommodationType}
                    showWhenEmpty
                  />
                  {leadbyId?.dates?.length === 1 ? (
                    <ProfileRow
                      label="Expected closure"
                      value={formatDate(leadbyId?.dates?.[0]?.date) || '---'}
                      showWhenEmpty
                    />
                  ) : leadbyId?.dates?.length > 1 ? (
                    <>
                      <ProfileRow
                        label="Previous closure date"
                        value={formatDate(leadbyId?.dates?.[1]?.date) || '---'}
                        showWhenEmpty
                      />
                      <ProfileRow
                        label="Next closure date"
                        value={formatDate(leadbyId?.dates?.[0]?.date) || '---'}
                        showWhenEmpty
                      />
                    </>
                  ) : (
                    <ProfileRow label="Expected closure" value="---" showWhenEmpty />
                  )}
                </div>
              </ExpandableProfileSection>
              </div>

              <ExpandableProfileSection
                title="Sales journey"
                subtitle={
                  salesJourneyAll[0]
                    ? `Latest: ${salesJourneyAll[0].title} (${salesJourneyAll[0].date})`
                    : 'No stage updates yet'
                }
                icon={MoveRight}
                preview={<TimelinePreview items={salesJourneyPreview} ghostItem={salesJourneyGhost} />}
                footerAction={
                  salesJourneyAll.length > 2 ? (
                    <button
                      type="button"
                      onClick={() => onHeaderTabChange?.(historyTabTarget)}
                      className="text-sm font-medium text-teal-700 hover:text-teal-800">
                      View full stage history →
                    </button>
                  ) : null
                }>
                <TimelinePreview
                  items={salesJourneyAll.length ? salesJourneyAll : salesJourneyItems}
                  ghostItem={salesJourneyGhost}
                />
              </ExpandableProfileSection>

              <ExpandableProfileSection
                title="Sales log"
                subtitle={
                  salesLogAll[0]
                    ? `Latest: ${salesLogAll[0].title} (${salesLogAll[0].date})`
                    : 'No call or visit notes yet'
                }
                icon={Receipt}
                preview={<TimelinePreview items={salesLogPreview} />}
                footerAction={
                  canAddStandaloneNote || salesLogAll.length > 2 ? (
                    <div className="flex flex-wrap items-center gap-3">
                      {canAddStandaloneNote ? (
                        <button
                          type="button"
                          onClick={() => {
                            setStandaloneNote('');
                            setShowAddNoteModal(true);
                          }}
                          className="text-sm font-medium text-teal-700 hover:text-teal-800">
                          + Add note
                        </button>
                      ) : null}
                      {salesLogAll.length > 2 ? (
                        <button
                          type="button"
                          onClick={() => onHeaderTabChange?.(historyTabTarget)}
                          className="text-sm font-medium text-teal-700 hover:text-teal-800">
                          View all logs →
                        </button>
                      ) : null}
                    </div>
                  ) : null
                }>
                <TimelinePreview items={salesLogAll.length ? salesLogAll : salesLogItems} />
              </ExpandableProfileSection>
              </div>

              <div className="space-y-3 min-w-0 xl:sticky xl:top-3 self-start">
              <ExpandableProfileSection
                key={`next-follow-up-${ID}`}
                title={isClosedLostLead ? 'Remarks' : 'Next follow-up'}
                subtitle={
                  isClosedLostLead
                    ? lostRemark || 'No drop reason recorded'
                    : followUpSubtitle
                }
                icon={Calendar}
                defaultExpanded>
                {isClosedLostLead ? (
                  <div className="space-y-2 text-sm">
                    <ProfileRow
                      label="Status"
                      value="Closed Lost"
                      highlight
                    />
                    <ProfileRow
                      label="Remarks"
                      value={lostRemark || '---'}
                      showWhenEmpty
                    />
                  </div>
                ) : hasFollowUp ? (
                  <>
                    {hasValidFollowUpDate && followUpOverdue?.overdue ? (
                      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                        <p className="text-sm font-semibold text-gray-900">
                          {formatDate(effectiveFollowUpDate) || '---'}
                        </p>
                        <p className="text-sm text-amber-800 mt-0.5 font-medium">
                          Overdue — schedule now
                        </p>
                      </div>
                    ) : null}
                    <div className="space-y-2 text-sm">
                      {!followUpOverdue?.overdue ? (
                        <ProfileRow
                          label="Date"
                          value={formatDate(effectiveFollowUpDate) || '---'}
                          highlight
                        />
                      ) : null}
                      <ProfileRow label="Agenda" value={followUpAgendaRaw} showWhenEmpty />
                      {followUpModeRaw !== '---' &&
                      followUpModeRaw.trim() !== followUpAgendaRaw.trim() ? (
                        <ProfileRow label="Mode" value={followUpModeRaw} />
                      ) : null}
                      <ProfileRow label="Owner" value={leadbyId?.sales?.name || 'Unassigned'} />
                    </div>

                    <div className="mt-4 flex items-center gap-3 flex-wrap pt-2 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={handleOpenFollowUpModal}
                        className="px-5 py-2 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">
                        Mark done
                      </button>
                      {hasValidFollowUpDate && (
                        <button
                          type="button"
                          onClick={() => {
                            if (!activeFollowUp) return;
                            setSelectedFollowUp(activeFollowUp);
                            const date = new Date(effectiveFollowUpDate || activeFollowUp.nextFollowUpDate);
                            const formattedDate = date.toISOString().slice(0, 16);
                            setFollowUpDate(formattedDate);
                            setShowUpdateFollowUpModal(true);
                          }}
                          className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-800 hover:bg-gray-50">
                          Reschedule
                        </button>
                      )}
                      {canAddStandaloneNote ? (
                        <button
                          type="button"
                          onClick={() => {
                            setStandaloneNote('');
                            setShowAddNoteModal(true);
                          }}
                          className="px-4 py-2 rounded-xl border border-teal-200 bg-teal-50 text-sm font-medium text-teal-800 hover:bg-teal-100">
                          Add note
                        </button>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col gap-3">
                    <p className="text-gray-600 text-sm">No follow-up scheduled yet.</p>
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={handleOpenFollowUpModal}
                        className="self-start px-5 py-2 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">
                        Schedule Follow-up
                      </button>
                      {canAddStandaloneNote ? (
                        <button
                          type="button"
                          onClick={() => {
                            setStandaloneNote('');
                            setShowAddNoteModal(true);
                          }}
                          className="self-start px-4 py-2 rounded-xl border border-teal-200 bg-teal-50 text-sm font-medium text-teal-800 hover:bg-teal-100">
                          Add note
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
              </ExpandableProfileSection>

              {hasEngagementData ? (
              <ExpandableProfileSection
                title="Engagement"
                subtitle={`Last activity: ${lastActivityLabel}`}
                icon={Activity}
                defaultExpanded={false}
                preview={
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-slate-50 px-2 py-2">
                      <p className="text-base font-bold text-gray-900 tabular-nums">{engagementStats.calls}</p>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mt-0.5">Calls</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-2 py-2">
                      <p className="text-base font-bold text-gray-900 tabular-nums">{engagementStats.whatsapp}</p>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mt-0.5">WhatsApp</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-2 py-2">
                      <p className="text-base font-bold text-gray-900 tabular-nums">{engagementStats.emails}</p>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mt-0.5">Emails</p>
                    </div>
                  </div>
                }>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-slate-50 px-2 py-2.5">
                    <p className="text-lg font-bold text-gray-900 tabular-nums">{engagementStats.calls}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-0.5">Calls</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 px-2 py-2.5">
                    <p className="text-lg font-bold text-gray-900 tabular-nums">{engagementStats.whatsapp}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-0.5">WhatsApp</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 px-2 py-2.5">
                    <p className="text-lg font-bold text-gray-900 tabular-nums">{engagementStats.emails}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-0.5">Emails</p>
                  </div>
                </div>
              </ExpandableProfileSection>
              ) : null}

              <NurtureRecommendationBanner
                leadId={leadIdNum}
                leadName={leadbyId?.user?.name}
                currentPstatus={leadbyId?.pstatus}
                nurtureRecommendation={aiInsight?.intelligence?.nurture_recommendation}
                onMoveToNurture={handleMoveToNurtureFromBanner}
                onScheduleFollowUp={() => setAiFollowUpOpen(true)}
                onAfterKeepActive={() => {
                  invalidateLeadAiCaches();
                  refetchLeadbyId();
                }}
              />
              </div>
            </div>
            </div>
          </>
        )}

        {leadbyId && type === 'deal' && activeHeaderTab === 'Pricing Details' && (
          <>
            {dealPricingDetailsToolbar}
            {(() => {
              const dealForPricing = closeDealData || leadbyId?.deal;
              const compare = getDealPricingCompare(
                dealForPricing,
                leadbyId?.underConsideration
              );
              const rateAreaUnit = resolveDealRateAreaUnit(
                compare,
                dealForPricing,
                Cookies.get('measurement_type')
              );
              const rateLabel = ratePerAreaLabel(rateAreaUnit);
              return (
                <>
                  <PricingDetailsSection
                    title="Finalized selling price"
                    subtitle="What management approved / the closed-won commercial — unit, PLC, extras, GST, and total.">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      <PricingMetricTile
                        label="Unit price"
                        value={formatCurrency(compare.finalized.unit_price)}
                      />
                      <PricingMetricTile
                        label="PLC price"
                        value={formatCurrency(compare.finalized.plc_price)}
                      />
                      <PricingMetricTile
                        label="Extra charges"
                        value={formatCurrency(compare.finalized.extra_charges)}
                        labelActions={
                          (closeDealData?.extra_charges_snapshot?.length > 0 ||
                            leadbyId?.deal?.extra_charges_snapshot?.length > 0) ? (
                            <button
                              type="button"
                              onClick={() => setShowExtraChargesBreakdownModal(true)}
                              className="rounded-md p-1 text-teal-800/80 transition-colors hover:bg-teal-100 hover:text-teal-900"
                              title="View breakdown">
                              <Info size={16} strokeWidth={2} aria-hidden />
                              <span className="sr-only">View extra charges breakdown</span>
                            </button>
                          ) : null
                        }
                      />
                      <PricingMetricTile
                        label="GST"
                        value={formatCurrency(compare.finalized.gst)}
                      />
                      <PricingMetricTile
                        label="Total / selling amount"
                        value={formatCurrency(compare.finalized.total)}
                        emphasis
                      />
                      {compare.rate_per_sqft?.finalized != null && (
                        <PricingMetricTile
                          label={rateLabel}
                          value={formatDealRatePerSqft(compare.rate_per_sqft.finalized)}
                        />
                      )}
                    </div>
                  </PricingDetailsSection>

                  <PricingDetailsSection
                    title="Listed vs proposed vs finalized"
                    subtitle="Inventory list rate, under-consideration proposal (if any), and the final closed amount — critical for approval.">
                    <div className="overflow-x-auto rounded-xl border border-slate-200">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-slate-600">
                          <tr>
                            <th className="px-3 py-2 font-medium">Component</th>
                            <th className="px-3 py-2 font-medium text-right">Listed</th>
                            <th className="px-3 py-2 font-medium text-right">Proposed</th>
                            <th className="px-3 py-2 font-medium text-right">Finalized</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ['Unit', 'unit_price'],
                            [rateLabel, 'rate_per_sqft'],
                            ['PLC', 'plc_price'],
                            ['Extra charges', 'extra_charges'],
                            ['GST', 'gst'],
                            ['Total', 'total']
                          ].map(([label, key]) => {
                            const isRate = key === 'rate_per_sqft';
                            const listed = isRate
                              ? compare.rate_per_sqft?.listed
                              : compare.listed?.[key];
                            const proposed = isRate
                              ? compare.rate_per_sqft?.proposed
                              : compare.proposed?.[key];
                            const finalized = isRate
                              ? compare.rate_per_sqft?.finalized
                              : compare.finalized?.[key];
                            if (isRate && listed == null && proposed == null && finalized == null) {
                              return null;
                            }
                            const fmt = isRate ? formatDealRatePerSqft : formatDealMoney;
                            return (
                            <tr key={key} className="border-t border-slate-100">
                              <td className="px-3 py-2 text-slate-700">{label}</td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {fmt(listed)}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {compare.proposed || (isRate && proposed != null)
                                  ? fmt(proposed)
                                  : '—'}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-900">
                                {fmt(finalized)}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {(compare.unit_discount !== 0 || compare.total_discount !== 0) && (
                      <p className="mt-3 text-sm text-slate-600">
                        Discount vs listed — unit:{' '}
                        <span className="font-medium text-slate-800">
                          {formatDealMoney(compare.unit_discount)}
                        </span>
                        {compare.total_discount !== compare.unit_discount ? (
                          <>
                            {' '}
                            · total:{' '}
                            <span className="font-medium text-slate-800">
                              {formatDealMoney(compare.total_discount)}
                            </span>
                          </>
                        ) : null}
                      </p>
                    )}
                  </PricingDetailsSection>
                </>
              );
            })()}
          </>
        )}

        {leadbyId && type === 'underConsideration' && activeHeaderTab === 'Pricing Details' && (
          <PricingDetailsSection
            title="Pricing details"
            subtitle="Commercial proposal for this under-consideration lead — unit price through total.">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <PricingMetricTile
                label="Unit price"
                value={formatCurrency(leadbyId?.underConsideration?.customer_proposal)}
              />
              <PricingMetricTile
                label="PLC price"
                value={formatCurrency(leadbyId?.underConsideration?.plc_price)}
              />
              <PricingMetricTile
                label="Extra charges"
                value={formatCurrency(leadbyId?.underConsideration?.extra_charges)}
              />
              <PricingMetricTile label="GST" value={formatCurrency(leadbyId?.underConsideration?.gst)} />
              <PricingMetricTile
                label="Total price"
                value={formatCurrency(leadbyId?.underConsideration?.total_price)}
                emphasis
              />
            </div>
          </PricingDetailsSection>
        )}
      </div>

      {/* Extra Charges Breakdown Modal (from close deal extra_charges_snapshot) */}
      {showExtraChargesBreakdownModal && (
        <Modal
          width={45}
          name="Extra Charges Breakdown"
          onClose={() => setShowExtraChargesBreakdownModal(false)}>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-gray-600">
              Total extra charges:{' '}
              {formatCurrency(closeDealData?.extra_charges ?? leadbyId?.deal?.extra_charges ?? 0)}
            </p>
            {closeDealData?.extra_charges_snapshot?.length > 0 ||
            leadbyId?.deal?.extra_charges_snapshot?.length > 0 ? (
              <div className="flex flex-col gap-2">
                {(
                  closeDealData?.extra_charges_snapshot ??
                  leadbyId?.deal?.extra_charges_snapshot ??
                  []
                ).map((item) => {
                  const base = Number(item.total_price) || 0;
                  const gstPct = Number(item.gst) || 0;
                  const gstAmount = (base * gstPct) / 100;
                  const totalWithGst = base + gstAmount;
                  return (
                    <div
                      key={item.id}
                      className="flex justify-between items-start p-3 bg-gray-50 rounded border border-gray-200 text-sm">
                      <span className="font-medium text-gray-900">
                        {item.name || 'Extra charge'}
                      </span>
                      <span className="text-gray-700 text-right">
                        ₹{totalWithGst.toLocaleString('en-IN', { maximumFractionDigits: 0 })}{' '}
                        <span className="text-gray-500">
                          (₹{base.toLocaleString('en-IN')} + ₹
                          {gstAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })} GST)
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No breakdown available.</p>
            )}
          </div>
        </Modal>
      )}

      {/* Follow Up Modal */}
      {showFollowUpModal && (
        <Modal
          isDirty
          confirmOnClose
          width={50}
          name="Follow Up"
          onClose={() => {
            setShowFollowUpModal(false);
            resetFollowUpForm();
          }}>
          <div className="flex flex-col gap-4">
            {/* Lead Info */}
            <div className="bg-gray-50 px-3 py-2.5 rounded-lg">
              <h3 className="font-semibold text-base text-gray-800 leading-snug">
                Follow Up with {leadbyId?.user?.name || 'Lead'}
              </h3>
              <p className="text-xs text-gray-600 mt-0.5">
                Project: {leadbyId?.projects?.[0]?.name || 'N/A'}
              </p>
            </div>

            {/* Stage Field */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stage <span className="text-red-500">*</span>
              </label>
              <Controller
                control={control}
                name="followUpStage"
                rules={{ required: 'Stage is required' }}
                render={({ field: { onChange, value } }) => (
                  <select
                    value={value || ''}
                    onChange={(e) => {
                      setSelectedStage(e.target.value);
                      setStage(e.target.value);
                      onChange(e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="" disabled>
                      Select Stage
                    </option>
                    {stageOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.title}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.followUpStage && (
                <p className="text-red-500 text-sm mt-1">{errors.followUpStage.message}</p>
              )}
            </div>

            {/* Log Field */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Log <span className="text-red-500">*</span>
              </label>
              <select
                value={log}
                onChange={(e) => {
                  const nextLog = e.target.value;
                  setLog(nextLog);
                  if (nextLog !== 'Other') {
                    setOtherLog('');
                  }
                  if (!requiresNextFollowUpFields({ stage, log: nextLog })) {
                    setFollowUpDate('');
                    setAgenda('');
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="" disabled>
                  Select Log
                </option>
                {logOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {log === 'Other' && (
                <input
                  type="text"
                  placeholder="Enter log here..."
                  value={otherLog}
                  onChange={(e) => setOtherLog(e.target.value)}
                  className="w-full px-3 py-2 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>

            {requiresNextFollowUpFields({ stage, log }) ? (
              <>
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {upcomingContactDateLabel(stage)} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {upcomingContactHelperText(stage) ? (
                    <p className="text-xs text-gray-500 mt-1">
                      {upcomingContactHelperText(stage)}
                    </p>
                  ) : null}
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {upcomingAgendaLabel(stage)} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    placeholder={upcomingAgendaPlaceholder(stage)}
                    value={agenda}
                    onChange={(e) => setAgenda(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            ) : (
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {closureNoteLabel({ stage, log })}{' '}
                  {requiresClosureNote({ stage, log }) && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <textarea
                  placeholder={closureNotePlaceholder({ stage, log })}
                  value={agenda}
                  onChange={(e) => setAgenda(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  No next follow-up is scheduled. This reason is saved to the lead&apos;s Stage
                  History &amp; logs.
                </p>
              </div>
            )}

            {/* Project Accommodation - Conditional */}
            {(stage === 'Commercials Shared' || stage === 'Negotiation') && (
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Accommodation <span className="text-red-500">*</span>
                </label>
                <Multiselect
                  options={projectAccommodationsData || []}
                  selectedValues={projectAccommodations}
                  onSelect={(selectedList) => setProjectAccommodations(selectedList)}
                  onRemove={(selectedList) => setProjectAccommodations(selectedList)}
                  displayValue="name"
                  placeholder={
                    leadbyId?.interested_projects?.[0]
                      ? 'Select project accommodations'
                      : 'Select a project first'
                  }
                  disabled={!leadbyId?.interested_projects?.[0]}
                  showCheckbox
                  style={{
                    multiselectContainer: { width: '100%' },
                    searchBox: {
                      minHeight: '40px',
                      fontSize: '1rem',
                      background: leadbyId?.interested_projects?.[0] ? 'white' : '#f5f5f5'
                    },
                    chips: { fontSize: '0.875rem', padding: '0.25rem 0.5rem' }
                  }}
                />
              </div>
            )}

            {/* File Upload */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachment (Optional)
              </label>
              <div className="flex items-center space-x-2">
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <input
                    id="file-upload"
                    type="file"
                    className="sr-only"
                    onChange={handleFileUpload}
                    accept="image/*,.pdf,.doc,.docx"
                    disabled={uploading}
                  />
                  {uploading ? 'Uploading...' : 'Choose File'}
                </label>
                {uploadedImage && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span className="truncate max-w-xs">{uploadedImage.name}</span>
                    <button
                      onClick={() => setUploadedImage(null)}
                      className="text-red-500 hover:text-red-700">
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={wantStageQualification}
                onChange={(e) => setWantStageQualification(e.target.checked)}
              />
              <span className="text-sm text-gray-700">
                Complete qualification questions (optional)
                <span className="mt-0.5 block text-xs text-gray-500">
                  Uses questions set on the Leads list. Answers become filterable tags. Leave off to
                  update the stage directly.
                </span>
              </span>
            </label>

            {/* Action Buttons */}
            <div className="w-full flex justify-end items-center space-x-4 mt-6 pt-4 border-t">
              <Button
                type="reset"
                onClick={() => {
                  setShowFollowUpModal(false);
                  resetFollowUpForm();
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button disabled={isSubmitting} type="button" onClick={handleCompleteFollowup}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Update Follow Up Date Modal */}
      {showUpdateFollowUpModal && (
        <Modal
          isDirty
          confirmOnClose
          width={40}
          name="Update Follow Up Date"
          overflowVisible
          onClose={() => {
            setShowUpdateFollowUpModal(false);
            setSelectedFollowUp(null);
            setFollowUpDate('');
          }}>
          <div className="flex flex-col space-y-4">
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Date & Time <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="w-full flex justify-end items-center space-x-4 mt-6 pt-4 border-t">
              <Button
                type="reset"
                onClick={() => {
                  setShowUpdateFollowUpModal(false);
                  setSelectedFollowUp(null);
                  setFollowUpDate('');
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button
                disabled={isUpdatingFollowUp}
                type="button"
                onClick={handleUpdateFollowUpDate}>
                {isUpdatingFollowUp ? 'Updating...' : 'Update'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Close Deal Modal */}
      {isCloseDealModalOpen && (
        <Modal
          width={50}
          name="Close Deal"
          confirmOnClose={true}
          isDirty={true}
          onClose={() => {
            setIsCloseDealModalOpen(false);
            refetchLeadbyId();
          }}>
          <ClosedWonCloseDeal
            onClose={() => {
              setIsCloseDealModalOpen(false);
              refetchLeadbyId();
            }}
            onSubmit={handleCloseDeal}
            isSubmitting={isSubmitting}
            selectedLead={ID}
            ucDetails={ucDetails}
          />
        </Modal>
      )}

      {/* Under Consideration Modal */}
      {isUnderConsiderationModalOpen && (
        <Modal
          width={50}
          name="Create Under Consideration"
          confirmOnClose={true}
          isDirty={true}
          onClose={() => {
            setIsUnderConsiderationModalOpen(false);
          }}>
          <UnderConsiderationCreate
            onClose={() => {
              setIsUnderConsiderationModalOpen(false);
            }}
            onSubmit={handleUnderConsideration}
            isSubmitting={isSubmitting}
            selectedLead={ID}
          />
        </Modal>
      )}

      {/* Stage Update Modal */}
      {showStageModal && (
        <Modal
          isDirty
          confirmOnClose
          width={40}
          name="Update Stage"
          onClose={() => {
            setShowStageModal(false);
            setStageToUpdate('');
            setProjectAccommodations([]);
            setWantStageQualification(false);
          }}>
          <div className="flex flex-col space-y-4">
            {/* Lead Info */}
            <div className="bg-gray-50 p-4 rounded-lg mb-2">
              <h3 className="font-semibold text-lg text-gray-800">
                Update Stage for {leadbyId?.user?.name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Current Stage: {displayStage || 'N/A'}
              </p>
            </div>

            {/* Stage Dropdown */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select New Stage <span className="text-red-500">*</span>
              </label>
              <select
                value={stageToUpdate}
                onChange={(e) => setStageToUpdate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="" disabled>
                  Select Stage
                </option>
                {stageOptions.map((option) => {
                  const isCurrent = option.id === displayStage;
                  return (
                    <option key={option.id} value={option.id} disabled={isCurrent}>
                      {isCurrent ? `${option.title} — current` : option.title}
                    </option>
                  );
                })}
              </select>
              {stageToUpdate === displayStage && displayStage ? (
                <p className="mt-1 text-xs text-amber-700">
                  You cannot move to the same stage. Select a different stage below.
                </p>
              ) : null}
            </div>

            {/* Project Accommodation - Conditional */}
            {(stageToUpdate === 'Commercials Shared' || stageToUpdate === 'Negotiation') && (
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Accommodation <span className="text-red-500">*</span>
                </label>
                <Multiselect
                  options={projectAccommodationsData || []}
                  selectedValues={projectAccommodations}
                  onSelect={(selectedList) => setProjectAccommodations(selectedList)}
                  onRemove={(selectedList) => setProjectAccommodations(selectedList)}
                  displayValue="name"
                  placeholder={
                    leadbyId?.interested_projects?.[0]
                      ? 'Select project accommodations'
                      : 'Select a project first'
                  }
                  disabled={!leadbyId?.interested_projects?.[0]}
                  showCheckbox
                  style={{
                    multiselectContainer: { width: '100%' },
                    searchBox: {
                      minHeight: '40px',
                      fontSize: '1rem',
                      background: leadbyId?.interested_projects?.[0] ? 'white' : '#f5f5f5'
                    },
                    chips: { fontSize: '0.875rem', padding: '0.25rem 0.5rem' }
                  }}
                />
              </div>
            )}

            {isUpcomingContactStage(stageToUpdate) && (
              <>
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {upcomingContactDateLabel(stageToUpdate)}{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {upcomingContactHelperText(stageToUpdate)}
                  </p>
                </div>
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {upcomingAgendaLabel(stageToUpdate)}{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    placeholder={upcomingAgendaPlaceholder(stageToUpdate)}
                    value={agenda}
                    onChange={(e) => setAgenda(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}

            <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={wantStageQualification}
                onChange={(e) => setWantStageQualification(e.target.checked)}
              />
              <span className="text-sm text-gray-700">
                Complete qualification questions (optional)
                <span className="mt-0.5 block text-xs text-gray-500">
                  Uses questions set on the Leads list. Answers become filterable tags. Leave off to
                  update the stage directly.
                </span>
              </span>
            </label>

            {/* Action Buttons */}
            <div className="w-full flex justify-end items-center space-x-4 mt-6 pt-4 border-t">
              <Button
                type="reset"
                onClick={() => {
                  setShowStageModal(false);
                  setStageToUpdate('');
                  setProjectAccommodations([]);
                  setWantStageQualification(false);
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button
                disabled={
                  isUpdatingStage || !stageToUpdate || stageToUpdate === displayStage
                }
                type="button"
                onClick={handleUpdateStage}>
                {isUpdatingStage ? 'Updating...' : 'Update'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {isEditLeadModalOpen && (
        <Modal
          width={50}
          confirmOnClose={true}
          isDirty={true}
          name="Edit Lead"
          onClose={() => setIsEditLeadModalOpen(false)}>
          <EditLeadForm
            onSubmit={handleEditLeadSubmit}
            onCancel={() => setIsEditLeadModalOpen(false)}
            isSubmitting={isUpdatingLeadDetails}
            AllProjects={projects}
            editingLead={leadbyId}
          />
        </Modal>
      )}
      {showPreleadFormModal && Number(leadbyId?.pre_lead_id || leadbyId?.preLead?.id) > 0 && (
        <PreleadOriginalFormModal
          preLeadId={Number(leadbyId?.pre_lead_id || leadbyId?.preLead?.id)}
          source={leadbyId?.source}
          prelead={leadbyId?.preLead ?? null}
          onClose={() => setShowPreleadFormModal(false)}
        />
      )}
      {isEditContactProfileOpen && canEditContactProfile && (
        <Modal
          width={52}
          confirmOnClose={true}
          isDirty={true}
          name="Edit contact profile"
          onClose={() => {
            if (isUpdatingContactProfile) return;
            setIsEditContactProfileOpen(false);
          }}>
          <EditContactProfileForm
            lead={leadbyId}
            onSubmit={handleContactProfileSubmit}
            onCancel={() => setIsEditContactProfileOpen(false)}
            isSubmitting={isUpdatingContactProfile}
          />
        </Modal>
      )}
      {showAddNoteModal && canAddStandaloneNote ? (
        <Modal
          isDirty={Boolean(standaloneNote.trim())}
          confirmOnClose
          width={40}
          name="Add note"
          onClose={closeAddNoteModal}>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-600">
              Saves a note on this lead without changing stage or follow-up.
            </p>
            <div className="w-full">
              <label
                htmlFor="standalone-lead-note"
                className="mb-2 block text-sm font-medium text-gray-700">
                Note <span className="text-red-500">*</span>
              </label>
              <textarea
                id="standalone-lead-note"
                rows={4}
                value={standaloneNote}
                onChange={(e) => setStandaloneNote(e.target.value)}
                placeholder="Enter note…"
                disabled={isAddingNote}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600 disabled:opacity-60"
              />
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <Button displayType="secondary" onClick={closeAddNoteModal} disabled={isAddingNote}>
                Cancel
              </Button>
              <Button
                displayType="primary"
                onClick={handleAddStandaloneNote}
                disabled={isAddingNote || !standaloneNote.trim()}
                isLoading={isAddingNote}>
                Save note
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
      {canDelete === 'no' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={`Cannot delete '${deleteResData?.name || 'this lead'}'`}
          onClose={() => {
            setCanDelete(null);
            setDeleteResData(null);
          }}
          width={45}>
          <div className="flex flex-col justify-center align-center">
            <p className="text-center">
              Looks like this lead has dependencies. Please delete the following stages to proceed.
            </p>
            <div className="flex justify-start align-middle flex-wrap bg-[#f5f7f8] p-2 rounded-[5px] mt-4">
              {deleteResData?.reason?.map((r, index) => (
                <span className="mt-[2px] mr-[4px] text-[#535353]" key={index}>
                  {index + 1}.{r}
                  {index < deleteResData.reason.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          </div>
          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="button"
              onClick={() => {
                setCanDelete(null);
                setDeleteResData(null);
              }}
              displayType="secondary">
              Close
            </Button>
          </div>
        </Modal>
      )}
      {canDelete === 'yes' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name="Confirmation"
          onClose={() => {
            setCanDelete(null);
            setDeleteResData(null);
          }}
          width={45}>
          <div className="flex flex-col justify-start items-center z-10">
            <h1 className="text-center text-[1.4rem] font-medium text-primary">
              Are you sure you want to delete the lead &apos;{deleteResData?.name}&apos;?
            </h1>
            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button
                type="button"
                onClick={() => {
                  setCanDelete(null);
                  setDeleteResData(null);
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirmDeleteLead}
                disabled={isDeletingLead}
                displayType="primary-red">
                {isDeletingLead ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {isCloneLeadModalOpen && (
        <Modal
          width={40}
          confirmOnClose={true}
          isDirty={true}
          name={`Duplicate Lead '${leadbyId?.user?.name || ''}'`}
          onClose={() => {
            setIsCloneLeadModalOpen(false);
            setSelectedCloneProjects([]);
          }}>
          <div className="flex flex-col justify-between items-start gap-4">
            <div className="flex flex-col gap-4 w-full">
              <div className="w-full">
                <label className="text-gray-600 mb-2 block">Select Projects</label>
                <Multiselect
                  key={`multiselect-clone-${ID}`}
                  options={cloneProjectOptions}
                  disable={!cloneProjectOptions.length}
                  selectedValues={selectedCloneProjects}
                  onSelect={(selectedList) => setSelectedCloneProjects(selectedList)}
                  onRemove={(selectedList) => setSelectedCloneProjects(selectedList)}
                  displayValue="name"
                  placeholder="Select projects"
                  emptyRecordMsg="No projects available"
                  showCheckbox
                  style={{
                    optionContainer: { border: '1px solid', backgroundColor: 'white' },
                    option: { color: 'black', backgroundColor: 'white' },
                    chips: { background: '#2A565E', color: 'white' }
                  }}
                />
              </div>
              <div className="flex justify-end items-center space-x-4 mt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setIsCloneLeadModalOpen(false);
                    setSelectedCloneProjects([]);
                  }}
                  displayType="secondary">
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmitCloneLead}
                  disabled={isCloningLead || selectedCloneProjects.length === 0}>
                  {isCloningLead ? 'Duplicating...' : 'Duplicate Lead'}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      <StageQualificationModal
        open={qualModalOpen}
        onClose={() => {
          setQualModalOpen(false);
          setPendingQualAction(null);
        }}
        leadId={Number(ID)}
        transition={pendingQualAction?.transition}
        leadName={leadbyId?.user?.name}
        targetStage={pendingQualAction?.targetStage}
        onSubmit={handleQualificationSubmit}
        isSubmitting={qualSubmitting || isUpdatingStage}
      />
    </div>
  );
};

export default SaleOverview;
